import React, { useState } from 'react'
import { css } from '@linaria/core'
import PropTypes from 'prop-types'
import { Separator, Paragraph, colors, Bleed, Image, LayoutBand, H1 } from '@fs/zion-ui'

const slideContentCss = css`
  padding: 0 64px;
`
const slideLayoutCss = css`
  height: 100%;
`

export default function HelpTutorialSlide({
  imageAlt,
  imageSrc,
  heading,
  headingSize = 'H5',
  contentSize = 'xs',
  content,
  imgBleed = true,
  imageProps,
  image,
}) {
  const [height, setHeight] = useState('44vh')
  const regularImg = <Image src={imageSrc} alt={imageAlt} height={height} noCrop positionY="top" {...imageProps} />
  const bleedableImg = <Bleed all>{regularImg}</Bleed>
  const nonBleedableImg = <LayoutBand color={colors.gray02}>{regularImg}</LayoutBand>
  const processedImage = imgBleed ? bleedableImg : nonBleedableImg

  const contentJsx = React.isValidElement(content) ? content : <Paragraph size={contentSize}>{content}</Paragraph>
  return (
    <div
      className={slideLayoutCss}
      ref={(el) => {
        if (el) {
          setHeight(el.clientHeight / 2)
        }
      }}
    >
      {image || processedImage}
      <Separator size="md" />
      <div className={slideContentCss}>
        <H1 size={headingSize}>{heading}</H1>
        {contentJsx}
      </div>
    </div>
  )
}

HelpTutorialSlide.propTypes = {
  /** Alt text for the slide's image */
  imageAlt: PropTypes.string,
  /** Valid URL to an image. Images should communicate something to the user and reinforce the text at a glance. */
  imageSrc: PropTypes.string,
  /** The heading text for the slide */
  heading: PropTypes.string.isRequired,
  /** Size of the heading text, corresponds with the zion-ui Heading sizes */
  headingSize: PropTypes.oneOf(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']),
  /** The actual contents of the slide. Should generally be kept brief (i.e. 1-2 sentences) */
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** Size of the content text, tee shirt sizes */
  contentSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  /** Allow image to bleed to the edges of the slide */
  imgBleed: PropTypes.bool,
  /** Object that has props for customizing the Image component */
  imageProps: PropTypes.object,
  /** Prebuilt image to use, instead of building one from the props */
  image: PropTypes.node,
}
